/**
 * @file Prev / Next through the list a detail page was opened from.
 *
 * `renderPager({ id, toPath })` returns the control (two chevron buttons
 * around a "3 / 148" position) or null when the id isn't in the remembered
 * list (see lib/list-memory.js). `bindPagerKeys` adds ← / → on the keyboard
 * for walking a list on a desktop; it stays out of the way while typing in a
 * field or while a modal is open, and unbinds when the page is torn down.
 */

import { el } from '../utils.js';
import { Icons } from '../icons.js';
import { navigate } from '../router.js';
import { listNeighbours } from './list-memory.js';

export function renderPager({ id, toPath }) {
  const n = listNeighbours(id);
  if (!n) return null;
  const btn = (targetId, icon, label) => el('button', {
    class: 'icon-btn',
    type: 'button',
    'aria-label': label,
    title: label,
    disabled: !targetId,
    onClick: () => { if (targetId) navigate(toPath(targetId)); },
  }, icon());
  return el('div', { class: 'pager', 'aria-label': `${n.label} order` },
    btn(n.prevId, Icons.chevron_left,  'Previous (←)'),
    el('span', { class: 'pager-pos' }, `${n.index + 1} / ${n.total}`),
    btn(n.nextId, Icons.chevron_right, 'Next (→)'),
  );
}

export function bindPagerKeys(host, { id, toPath }) {
  const n = listNeighbours(id);
  if (!n) return;
  const onKey = (e) => {
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
    if (e.metaKey || e.ctrlKey || e.altKey) return;
    const t = e.target;
    if (t && (t.closest('input, textarea, select, [contenteditable="true"]'))) return;
    if (document.querySelector('.modal-backdrop.open, .sheet-backdrop')) return;
    const target = e.key === 'ArrowLeft' ? n.prevId : n.nextId;
    if (!target) return;
    e.preventDefault();
    navigate(toPath(target));
  };
  document.addEventListener('keydown', onKey);
  host.addEventListener('cleanup', () => document.removeEventListener('keydown', onKey), { once: true });
}
